import {
  Button,
  CustomRequestOption,
  IconButton,
  isFile,
  Separator,
  Slider,
  Switch,
  Upload,
  UploadState,
} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import {FinancingCompositionScene} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {notifyUnsupportedImageFormat} from '../../../utils/notifyUnsupportedImageFormat';
import {compositionEditorLimits} from '../constants/compositionEditorLimits';
import {IMAGE_LAYER_ACCEPT} from '../constants/imageLayerAccept';
import {LayerAlignment} from '../types/CompositionEditorAction';
import {FinancingTemplateLook} from '../utils/createFinancingTemplateScene';
import {ColorField} from './ColorField';
import {DraftIntegerField} from './DraftIntegerField';

interface EditorToolbarProps extends TestIdProps {
  canvas: FinancingCompositionScene['canvas'];
  hasSelectedLayer: boolean;
  canAddLayer: boolean;
  canAddBadge: boolean;
  // Present in financing mode only; false once the scene has its single photo slot.
  canAddPhotoSlot?: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onAddText: () => void;
  onAddImage: (fileId: string, url: string) => void;
  onGenerateBadge: () => void;
  onAddRect: () => void;
  onAddBadge: () => void;
  onAddPhotoSlot?: () => void;
  // Present in financing mode only.
  onStartFromTemplate?: (look: FinancingTemplateLook) => void;
  onAlign: (alignment: LayerAlignment) => void;
  onUndo: () => void;
  onRedo: () => void;
  onCanvasSizeChange: (width: number, height: number) => void;
  onBackgroundChange: (background: FinancingCompositionScene['canvas']['background']) => void;
}

const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';
const OPACITY_PERCENT = 100;

const ALIGNMENTS: {alignment: LayerAlignment; label: string}[] = [
  {alignment: 'left', label: i18n.t('entity.watermark.composition.labels.alignLeft')},
  {alignment: 'hCenter', label: i18n.t('entity.watermark.composition.labels.alignHCenter')},
  {alignment: 'right', label: i18n.t('entity.watermark.composition.labels.alignRight')},
  {alignment: 'top', label: i18n.t('entity.watermark.composition.labels.alignTop')},
  {alignment: 'vCenter', label: i18n.t('entity.watermark.composition.labels.alignVCenter')},
  {alignment: 'bottom', label: i18n.t('entity.watermark.composition.labels.alignBottom')},
];

const TEMPLATE_LOOKS: {look: FinancingTemplateLook; label: string}[] = [
  {look: 'infoPanel', label: i18n.t('entity.promoPhotos.composition.templates.infoPanel')},
  {look: 'overlayPanel', label: i18n.t('entity.promoPhotos.composition.templates.overlayPanel')},
];

export function EditorToolbar(props: EditorToolbarProps) {
  const [uploadFile, {isLoading: isUploadingImage}] = useUploadFileMutation();
  const background = props.canvas.background;

  const handleImageUpload = (option: CustomRequestOption) => {
    if (!isFile(option.file)) {
      return;
    }
    uploadFile({file: option.file})
      .unwrap()
      .then((uploaded) => {
        props.onAddImage(uploaded.fileId, uploaded.fileUri);
        option.onSuccess?.({});
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        option.onError?.(new Error('Image upload failed'));
      });
  };

  return (
    <VStack spacing={3}>
      <Show when={props.onStartFromTemplate}>
        {(onStartFromTemplate) => (
          <>
            <Heading size={5}>
              {i18n.t('entity.promoPhotos.composition.templates.startFromTemplate')}
            </Heading>
            <VStack spacing={1} align="flex-start">
              {TEMPLATE_LOOKS.map((item) => (
                <Button
                  key={item.look}
                  variant="ghostLink"
                  leftIcon="action/dashboard"
                  title={item.label}
                  onClick={() => onStartFromTemplate(item.look)}
                  data-testid={suffixTestId(`template-${item.look}`, props)}
                />
              ))}
            </VStack>
            <Separator spacing={0} />
          </>
        )}
      </Show>
      <Heading size={5}>{i18n.t('entity.watermark.composition.labels.addLayer')}</Heading>
      <VStack spacing={1} align="flex-start">
        <Button
          variant="ghostLink"
          leftIcon="content/text_format"
          title={i18n.t('entity.watermark.composition.actions.addText')}
          isDisabled={!props.canAddLayer}
          onClick={props.onAddText}
          data-testid={suffixTestId('addText', props)}
        />
        <Upload
          type="button"
          accept={IMAGE_LAYER_ACCEPT}
          shouldValidateAccept
          buttonVariant="ghostLink"
          uploadIcon="image/image"
          uploadText={i18n.t('entity.watermark.composition.actions.addImage')}
          uploadingText={i18n.t('general.labels.uploadingWithDots')}
          uploadState={isUploadingImage ? UploadState.Uploading : UploadState.Idle}
          isDisabled={!props.canAddLayer}
          customRequest={handleImageUpload}
          onError={notifyUnsupportedImageFormat}
          data-testid={suffixTestId('addImage', props)}
        />
        <Button
          variant="ghostLink"
          leftIcon="action/magic_wand"
          title={i18n.t('entity.watermark.composition.ai.actions.generate')}
          isDisabled={!props.canAddLayer}
          onClick={props.onGenerateBadge}
          data-testid={suffixTestId('generateBadge', props)}
        />
        <Button
          variant="ghostLink"
          leftIcon="image/crop"
          title={i18n.t('entity.watermark.composition.actions.addRect')}
          isDisabled={!props.canAddLayer}
          onClick={props.onAddRect}
          data-testid={suffixTestId('addRect', props)}
        />
        <Button
          variant="ghostLink"
          leftIcon="content/flag"
          title={i18n.t('entity.watermark.composition.actions.addBadge')}
          isDisabled={!props.canAddBadge}
          onClick={props.onAddBadge}
          data-testid={suffixTestId('addBadge', props)}
        />
        <Show when={isNotNil(props.onAddPhotoSlot)}>
          <Button
            variant="ghostLink"
            leftIcon="image/photo_camera"
            title={i18n.t('entity.promoPhotos.composition.actions.addPhotoSlot')}
            isDisabled={!props.canAddLayer || !props.canAddPhotoSlot}
            onClick={props.onAddPhotoSlot}
            data-testid={suffixTestId('addPhotoSlot', props)}
          />
        </Show>
        <Show when={!props.canAddBadge}>
          <Text size="xSmall" color="warning" data-testid={suffixTestId('layerLimit', props)}>
            {i18n.t('entity.watermark.composition.labels.layerLimitReached', {
              count: compositionEditorLimits.maxLayers,
            })}
          </Text>
        </Show>
      </VStack>

      <Separator spacing={0} />

      <Heading size={5}>{i18n.t('general.labels.history')}</Heading>
      <HStack spacing={2}>
        <IconButton
          icon="content/undo"
          isDisabled={!props.canUndo}
          onClick={props.onUndo}
          data-testid={suffixTestId('undo', props)}
        />
        <IconButton
          icon="content/redo"
          isDisabled={!props.canRedo}
          onClick={props.onRedo}
          data-testid={suffixTestId('redo', props)}
        />
      </HStack>

      <Separator spacing={0} />

      <Heading size={5}>{i18n.t('entity.watermark.composition.labels.alignToCanvas')}</Heading>
      <HStack spacing={1} wrap>
        {ALIGNMENTS.map((item) => (
          <Button
            key={item.alignment}
            size="small"
            variant="outlined"
            title={item.label}
            isDisabled={!props.hasSelectedLayer}
            onClick={() => props.onAlign(item.alignment)}
            data-testid={suffixTestId(`align-${item.alignment}`, props)}
          />
        ))}
      </HStack>

      <Separator spacing={0} />

      <Heading size={5}>{i18n.t('entity.watermark.composition.labels.canvas')}</Heading>
      <HStack spacing={2}>
        <Box flex={1} minWidth={0}>
          <DraftIntegerField
            label={i18n.t('general.labels.width')}
            value={props.canvas.width}
            onCommit={(width) => props.onCanvasSizeChange(width, props.canvas.height)}
            data-testid={suffixTestId('canvasWidth', props)}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <DraftIntegerField
            label={i18n.t('general.labels.height')}
            value={props.canvas.height}
            onCommit={(height) => props.onCanvasSizeChange(props.canvas.width, height)}
            data-testid={suffixTestId('canvasHeight', props)}
          />
        </Box>
      </HStack>
      <Switch
        label={i18n.t('entity.watermark.composition.labels.transparentBackground')}
        value={isNil(background)}
        onChange={(isTransparent) =>
          props.onBackgroundChange(
            isTransparent ? null : {color: DEFAULT_BACKGROUND_COLOR, opacity: 1}
          )
        }
        data-testid={suffixTestId('transparentBackground', props)}
      />
      <Show when={isNotNil(background)}>
        <ColorField
          label={i18n.t('entity.watermark.composition.labels.backgroundColor')}
          value={background?.color ?? DEFAULT_BACKGROUND_COLOR}
          onChange={(color) => props.onBackgroundChange({opacity: background?.opacity ?? 1, color})}
          data-testid={suffixTestId('backgroundColor', props)}
        />
        <Slider
          label={i18n.t('entity.watermark.composition.labels.backgroundOpacity')}
          value={Math.round((background?.opacity ?? 1) * OPACITY_PERCENT)}
          min={0}
          max={OPACITY_PERCENT}
          step={1}
          ticks={2}
          onChange={(value) =>
            props.onBackgroundChange({
              color: background?.color ?? DEFAULT_BACKGROUND_COLOR,
              opacity: value / OPACITY_PERCENT,
            })
          }
          data-testid={suffixTestId('backgroundOpacity', props)}
        />
      </Show>
    </VStack>
  );
}
